import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { userService } from '../../../_services';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Trash2, Edit2, Star, Search } from 'lucide-react';
import GrandparentConfirmDialog from './GrandparentConfirmDialog';

/** Match Bridge Asign_Edit_Group: resolve selected override user for grandparent_editgroup API. */
function resolveOverrideUser(overrideRow, allOverrideList, repsList) {
    if (!overrideRow?.override) return null;
    const username = overrideRow.override;
    let match =
        allOverrideList.find((u) => u.override === username) ||
        allOverrideList.find((u) => u.userName === username) ||
        repsList.find((u) => u.userName === username);
    if (!match && overrideRow.email) {
        const email = overrideRow.email.toLowerCase();
        match =
            allOverrideList.find((u) => u.email?.toLowerCase() === email) ||
            repsList.find((u) => u.email?.toLowerCase() === email);
    }
    if (!match && overrideRow.fullName) {
        match =
            allOverrideList.find((u) => u.fullName === overrideRow.fullName) ||
            repsList.find((u) => u.fullName === overrideRow.fullName);
    }
    return match;
}

function getRepUserNameForGrandparent(user, overrideRow) {
    return user?.override ?? user?.userName ?? overrideRow?.override ?? '';
}

function getRepEmailForGrandparent(user, overrideRow) {
    return user?.email ?? overrideRow?.email ?? '';
}

function normalizeGrandparentApiRows(data) {
    const list = Array.isArray(data) ? data : data ? [data] : [];
    return list.map((row) => ({
        parent_UserName: row.parent_UserName ?? row.Parent_UserName,
        parent_FullName: row.parent_FullName ?? row.Parent_FullName,
        parent_GcComm: row.parent_GcComm ?? row.Parent_GcComm,
        parent_IsAllowedForGC: row.parent_IsAllowedForGC ?? row.Parent_IsAllowedForGC,
    }));
}

const AssignEditGroupDialog = ({ open, onClose, onSave, poptype, title, userid, groupnumber }) => {
    const [loading, setLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [submitMessage, setSubmitMessage] = useState('');

    const [reps, setReps] = useState([]);
    const [allOverride, setAllOverride] = useState([]);
    const [overrides, setOverrides] = useState([]);

    const [selectedRep, setSelectedRep] = useState(null);
    const [repCommission, setRepCommission] = useState('');
    const [repSearchTerm, setRepSearchTerm] = useState('');
    const [showRepDropdown, setShowRepDropdown] = useState(false);

    const [editingRowIndex, setEditingRowIndex] = useState(null);
    const [savingRowIndex, setSavingRowIndex] = useState(null);
    const [editingSnapshot, setEditingSnapshot] = useState(null); // snapshot of row before editing
    const [isNewRow, setIsNewRow] = useState(false); // true if the editing row was just added (not existing)
    const [overrideSearchTerms, setOverrideSearchTerms] = useState({});
    const [showOverrideDropdowns, setShowOverrideDropdowns] = useState({});

    const [grandparentEditPopup, setGrandparentEditPopup] = useState(false);
    const [grandparentsList, setGrandparentsList] = useState([]);
    const [currentOverrideData, setCurrentOverrideData] = useState(null);

    // ── Portal dropdown positioning ─────────────────────────────────────────
    const repInputWrapperRef = useRef(null);
    const overrideInputWrapperRefs = useRef({});
    const [repDropdownPos, setRepDropdownPos] = useState(null);
    const [overrideDropdownPositions, setOverrideDropdownPositions] = useState({});

    const getDropdownPosition = (el) => {
        if (!el) return null;
        const rect = el.getBoundingClientRect();
        const DROPDOWN_HEIGHT = 108; // max-h-[100px] + a little breathing room
        const spaceBelow = window.innerHeight - rect.bottom;
        const openUpward = spaceBelow < DROPDOWN_HEIGHT;
        return openUpward
            ? { bottom: window.innerHeight - rect.top + 4, left: rect.left, width: rect.width, openUpward: true }
            : { top: rect.bottom + 4,                      left: rect.left, width: rect.width, openUpward: false };
    };

    useEffect(() => {
        if (open) {
            loadData();
        } else {
            // Reset state when dialog closes
            resetState();
        }
    }, [open, poptype, userid, groupnumber]);

    const resetState = () => {
        setLoading(false);
        setSaveLoading(false);
        setSubmitMessage('');
        setReps([]);
        setAllOverride([]);
        setOverrides([]);
        setSelectedRep(null);
        setRepCommission('');
        setRepSearchTerm('');
        setShowRepDropdown(false);
        setEditingRowIndex(null);
        setSavingRowIndex(null);
        setEditingSnapshot(null);
        setIsNewRow(false);
        setOverrideSearchTerms({});
        setShowOverrideDropdowns({});
        setGrandparentEditPopup(false);
        setGrandparentsList([]);
        setCurrentOverrideData(null);
    };

    const loadData = async () => {
        setLoading(true);
        try {
            const usermetaData = window.atob(localStorage.getItem('ajs_user_id'));
            const [userId, userName] = usermetaData.split(':');

            if (poptype === 'assign') {
                const apidata = await userService.GetRepsOverrides(userId, userName);
                if (apidata.length > 0 && apidata[0].reps.length > 0) {
                    setReps(apidata[0].reps);
                    setAllOverride(apidata[0].allOverrides);
                    // Leave Select User blank � user must choose manually
                    setSelectedRep(null);
                    setRepSearchTerm('');
                    setRepCommission('');
                }
            } else if (poptype === 'edit') {
                const apidata = await userService.GetOverridesOfUser(userId, userName, userid, groupnumber);
                if (apidata.length > 0 && apidata[0].reps.length > 0) {
                    setReps(apidata[0].reps);
                    setAllOverride(apidata[0].allOverrides);
                    const rep = apidata[0].reps[0];
                    setSelectedRep(rep);
                    setRepSearchTerm(rep.fullName);
                    setRepCommission(rep.comm);
                    setOverrides(apidata[0].overrides || []);
                }
            }
        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddOverride = () => {
        setSubmitMessage('');

        // Check if there are any unsaved rows (in editing mode)
        if (editingRowIndex !== null) {
            setSubmitMessage('Please Save or remove override');
            return;
        }

        // Check if there's a blank row
        if (overrides.length > 0) {
            const hasBlankRow = overrides.some(o => !o.override || !o.comm);
            if (hasBlankRow) {
                setSubmitMessage('Please fill & add existing row first');
                return;
            }
        }

        const newOverride = { override: '', comm: '', fullName: '', parentType: 'P' };
        setOverrides([...overrides, newOverride]);
        setEditingRowIndex(overrides.length);
        setEditingSnapshot(null); // no snapshot � new row
        setIsNewRow(true);
    };

    const handleRemoveOverride = (index) => {
        setSubmitMessage('');
        const newOverrides = [...overrides];
        newOverrides.splice(index, 1);
        setOverrides(newOverrides);
        if (editingRowIndex === index) {
            setEditingRowIndex(null);
        }
    };

    const handleEditOverride = (index) => {
        // Save snapshot so Cancel can restore original values
        setEditingSnapshot({ ...overrides[index] });
        setIsNewRow(false);
        setEditingRowIndex(index);
        setOverrideSearchTerms(prev => ({
            ...prev,
            [index]: overrides[index].fullName || '',
        }));
        const resolved = resolveOverrideUser(overrides[index], allOverride, reps);
        if (resolved) {
            setCurrentOverrideData(resolved);
        }
    };

    const handleCancelEditOverride = (index) => {
        if (isNewRow) {
            // New row added via "Add Override" � remove it entirely
            const newOverrides = [...overrides];
            newOverrides.splice(index, 1);
            setOverrides(newOverrides);
        } else if (editingSnapshot) {
            // Existing row � restore original values from snapshot
            const newOverrides = [...overrides];
            newOverrides[index] = { ...editingSnapshot };
            setOverrides(newOverrides);
            // Also restore the search term display
            const newSearchTerms = { ...overrideSearchTerms };
            newSearchTerms[index] = editingSnapshot.fullName;
            setOverrideSearchTerms(newSearchTerms);
        }
        // Close dropdown if open
        const newDropdowns = { ...showOverrideDropdowns };
        newDropdowns[index] = false;
        setShowOverrideDropdowns(newDropdowns);
        setEditingRowIndex(null);
        setEditingSnapshot(null);
        setIsNewRow(false);
        setSubmitMessage('');
    };

    const handleOverrideSelectUser = (index, user) => {
        setSubmitMessage('');
        const newOverrides = [...overrides];
        newOverrides[index] = {
            ...newOverrides[index],
            override: user.override ?? user.userName, // reps use userName; allOverride uses override
            fullName: user.fullName,
            comm: user.comm,
            email: user.email ?? newOverrides[index].email,
        };
        setOverrides(newOverrides);
        setCurrentOverrideData(user);

        // Close dropdown
        const newShowDropdowns = { ...showOverrideDropdowns };
        newShowDropdowns[index] = false;
        setShowOverrideDropdowns(newShowDropdowns);

        // Clear search term
        const newSearchTerms = { ...overrideSearchTerms };
        newSearchTerms[index] = user.fullName;
        setOverrideSearchTerms(newSearchTerms);
    };

    const handleSaveOverride = async (index) => {
        setSubmitMessage('');
        const override = overrides[index];

        if (!override.override || !override.comm) {
            setSubmitMessage('Please fill all fields');
            return;
        }

        setSavingRowIndex(index);
        try {
            const resolved =
                resolveOverrideUser(override, allOverride, reps) ||
                currentOverrideData;
            const repUserName = getRepUserNameForGrandparent(resolved, override);
            const repEmail = getRepEmailForGrandparent(resolved, override);

            if (!repUserName) {
                setSubmitMessage('Please select a valid user');
                return;
            }

            const resolvedForTitle = resolved || { ...override, email: repEmail, override: repUserName };
            setCurrentOverrideData(resolvedForTitle);

            if (repEmail && override.email !== repEmail) {
                const withEmail = [...overrides];
                withEmail[index] = { ...withEmail[index], email: repEmail };
                setOverrides(withEmail);
            }

            // Bridge always calls grandparent_editgroup on Add/Save override row
            await checkGrandparent(repUserName, repEmail);
        } finally {
            setSavingRowIndex(null);
            setEditingRowIndex(null);
            setEditingSnapshot(null);
            setIsNewRow(false);
        }
    };

    const checkGrandparent = async (repUserName, repEmailId) => {
        try {
            const usermetaData = window.atob(localStorage.getItem('ajs_user_id'));
            const [userId, userName] = usermetaData.split(':');

            const raw = await userService.grandparentEditgroup(userId, userName, repUserName, repEmailId);
            const apidata = normalizeGrandparentApiRows(raw);
            if (apidata.length > 0) {
                setGrandparentsList(apidata);
                setGrandparentEditPopup(true);
            }
        } catch (error) {
            console.error('Error checking grandparent:', error);
        }
    };

    const handleAddGrandParent = () => {
        const filteredGrandparents = grandparentsList
            .filter(item => item.parent_IsAllowedForGC == 1)
            .map(item => ({
                comm: item.parent_GcComm,
                email: null,
                fullName: item.parent_FullName,
                override: item.parent_UserName,
                parentType: 'GP'
            }));

        setOverrides([...overrides, ...filteredGrandparents]);
        setGrandparentEditPopup(false);
        setGrandparentsList([]);
        setCurrentOverrideData(null);
    };

    const handleOverrideCommChange = (index, value) => {
        const newOverrides = [...overrides];
        newOverrides[index] = {
            ...newOverrides[index],
            comm: value
        };
        setOverrides(newOverrides);
    };

    const handleSaveSubmit = async () => {
        setSaveLoading(true);
        setSubmitMessage('');

        // Check if there are unsaved rows
        if (editingRowIndex !== null) {
            setSubmitMessage('Please Save or remove override');
            setSaveLoading(false);
            return;
        }

        // Validate rep selection and commission (assign mode)
        if (poptype === 'assign' && !selectedRep) {
            setSubmitMessage('Please select a user');
            setSaveLoading(false);
            return;
        }
        if (poptype === 'assign' && !repCommission) {
            setSubmitMessage('Please enter commission');
            setSaveLoading(false);
            return;
        }

        // Build override table
        const overrideTable = overrides.map(o => ({
            Override: o.override,
            Comm: o.comm,
            ParentType: o.parentType === 'GP' ? 'Grandparent' : 'Parent'
        }));

        // Validate grandparents count
        const countGrandparents = overrideTable.filter(item => item.ParentType === 'Grandparent').length;
        if (countGrandparents > 1) {
            setSubmitMessage('Multiple grandparents are not allowed');
            setSaveLoading(false);
            return;
        }

        // Validate commission values
        for (const o of overrides) {
            if (o.comm.toString().split('.').length > 2) {
                setSubmitMessage('Please enter valid commission');
                setSaveLoading(false);
                return;
            }
            if (parseFloat(o.comm) > 2.75) {
                setSubmitMessage('Commission should not greater than Wholesaler Commission');
                setSaveLoading(false);
                return;
            }
        }

        // Call API
        try {
            const usermetaData = window.atob(localStorage.getItem('ajs_user_id'));
            const [userId, userName] = usermetaData.split(':');

            const un = selectedRep?.userName ?? '';
            const comm = String(repCommission).trim();
            const uid = poptype === 'assign' ? '-1' : String(userid);
            const overrideJson = JSON.stringify(overrideTable);

            // Validate self override and duplicates
            const overrideUsernames = overrideTable.map(o => o.Override);
            if (overrideUsernames.includes(un)) {
                setSubmitMessage('User cannot be its self override');
                setSaveLoading(false);
                return;
            }

            const duplicates = overrideUsernames.filter((item, index) => overrideUsernames.indexOf(item) !== index);
            if (duplicates.length > 0) {
                setSubmitMessage('Same user cannot be override multiple times');
                setSaveLoading(false);
                return;
            }

            // Read auth tokens exactly as the reference CallApi does
            let userToken = '';
            let logonTime = '';
            try {
                const raw_dt = localStorage.getItem('_Secure_3PSID_DT');
                const raw_tkn = localStorage.getItem('_Host_Pro_Hckare_TKN');
                if (raw_dt && raw_tkn) {
                    logonTime = window.atob(raw_dt);
                    userToken = window.atob(raw_tkn);
                }
            } catch (_) {}

            // Make the same raw fetch call as the reference branch's CallApi_withheader
            const response = await fetch('/api/Account/gn_engage', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'requestHeader_TKN': userToken,
                    'requestHeader_DT': logonTime,
                    'OverridesJson': overrideJson,
                },
                body: JSON.stringify({ UserId: userId, UserName: userName, gn: groupnumber, un, comm, uid }),
            });

            let apidata = [];
            try {
                const text = await response.text();
                apidata = text ? JSON.parse(text) : [];
            } catch (_) {
                apidata = [];
            }

            if (apidata.length > 0 && apidata[0].code === '1') {
                setSaveLoading(false);
                onSave();
            } else {
                setSubmitMessage('Something went Wrong!');
                setSaveLoading(false);
            }
        } catch (error) {
            console.error('Error saving:', error);
            setSubmitMessage('Something went Wrong!');
            setSaveLoading(false);
        }
    };

    const filteredReps = reps.filter(rep => {
        const term = repSearchTerm.toLowerCase();
        return (
            (rep.fullName || '').toLowerCase().includes(term) ||
            (rep.email || '').toLowerCase().includes(term)
        );
    });

    const getFilteredOverrides = (index) => {
        const searchTerm = (overrideSearchTerms[index] || '').toLowerCase();
        // Assign: Bridge lists reps; edit: allOverrides (both include email when available)
        const dataSource =
            poptype === 'assign'
                ? (reps.length ? reps : allOverride)
                : (allOverride.length ? allOverride : reps);
        return dataSource.filter(item =>
            (item.fullName || '').toLowerCase().includes(searchTerm) ||
            (item.email || '').toLowerCase().includes(searchTerm) ||
            (item.userName || item.override || '').toLowerCase().includes(searchTerm)
        );
    };

    return (
        <>
            <Dialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen) onClose(); }}>
                <DialogContent
                    className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
                    onPointerDownOutside={(e) => e.preventDefault()}
                    onInteractOutside={(e) => e.preventDefault()}
                >
                    <DialogHeader>
                        <DialogTitle>{title}</DialogTitle>
                    </DialogHeader>

                    {loading ? (
                        <div className="flex items-center justify-center p-8">
                            <Loader2 className="h-8 w-8 animate-spin text-accent" />
                        </div>
                    ) : (
                        <div className="flex-1 space-y-6 px-1 overflow-y-auto">
                            {/* Rep Selection and Commission */}
                            {reps.length > 0 && (
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Select User</Label>
                                        {poptype === 'assign' ? (
                                            <div className="relative">
                                                <div className="relative" ref={repInputWrapperRef}>
                                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                                                    <Input
                                                        className="pl-9"
                                                        placeholder="Search by name or email..."
                                                        autoComplete="off"
                                                        value={repSearchTerm}
                                                        onChange={(e) => { setRepSearchTerm(e.target.value); setShowRepDropdown(true); }}
                                                        onFocus={() => {
                                                            setRepDropdownPos(getDropdownPosition(repInputWrapperRef.current));
                                                            setShowRepDropdown(true);
                                                        }}
                                                        onBlur={() => setTimeout(() => setShowRepDropdown(false), 200)}
                                                    />
                                                </div>
                                                {showRepDropdown && filteredReps.length > 0 && repDropdownPos && createPortal(
                                                    <div
                                                        className="fixed z-[9999] bg-popover border border-border rounded-md shadow-lg max-h-[100px] overflow-y-auto"
                                                        style={{ ...(repDropdownPos.openUpward ? { bottom: repDropdownPos.bottom } : { top: repDropdownPos.top }), left: repDropdownPos.left, width: repDropdownPos.width, pointerEvents: 'auto' }}
                                                    >
                                                        {filteredReps.map((rep, i) => (
                                                            <button
                                                                key={i}
                                                                type="button"
                                                                className="w-full text-left px-3 py-2 text-sm hover:bg-accent flex flex-col items-start gap-0.5 sm:flex-row sm:items-center sm:justify-between sm:gap-2"
                                                                onMouseDown={(e) => e.preventDefault()}
                                                                onClick={() => {
                                                                    setSelectedRep(rep);
                                                                    setRepSearchTerm(rep.fullName);
                                                                    setRepCommission(rep.comm ?? '');
                                                                    setShowRepDropdown(false);
                                                                }}
                                                            >
                                                                <span className="font-medium">{rep.fullName}</span>
                                                                <span className="text-xs text-muted-foreground sm:truncate">{rep.email}</span>
                                                            </button>
                                                        ))}
                                                    </div>,
                                                    document.body
                                                )}
                                                {showRepDropdown && repSearchTerm.length > 0 && filteredReps.length === 0 && repDropdownPos && createPortal(
                                                    <div
                                                        className="fixed z-[9999] bg-popover border border-border rounded-md shadow-lg px-3 py-2 text-sm text-muted-foreground"
                                                        style={{ ...(repDropdownPos.openUpward ? { bottom: repDropdownPos.bottom } : { top: repDropdownPos.top }), left: repDropdownPos.left, width: repDropdownPos.width, pointerEvents: 'auto' }}
                                                    >
                                                        No user found.
                                                    </div>,
                                                    document.body
                                                )}
                                            </div>
                                        ) : (
                                            <Input
                                                value={repSearchTerm}
                                                disabled
                                                className="bg-muted"
                                            />
                                        )}
                                    </div>

                                    <div className="space-y-2">
                                        <Label>Commission $</Label>
                                        <Input
                                            type="text"
                                            value={repCommission}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (/^[0-9.]*$/.test(value)) {
                                                    setRepCommission(value);
                                                }
                                            }}
                                            disabled={poptype === 'edit'}
                                            placeholder="Enter commission"
                                            className={poptype === 'edit' ? 'bg-muted' : ''}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Overrides Section */}
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-lg font-semibold">Override</h3>
                                    {overrides.length > 0 && (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={handleAddOverride}
                                            disabled={editingRowIndex !== null}
                                        >
                                            Add New Override
                                        </Button>
                                    )}
                                </div>

                                {overrides.length === 0 ? (
                                    <div className="text-center py-8 border rounded-lg bg-muted/30">
                                        <p className="mb-4 text-muted-foreground">No Override added yet</p>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={handleAddOverride}
                                        >
                                            Add New Override
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="border rounded-md overflow-hidden">
                                        {/* Sticky Header Table */}
                                        <div className="hidden md:grid md:grid-cols-[minmax(200px,2fr)_minmax(150px,1fr)_160px] gap-4 px-3 py-2 bg-muted font-semibold text-sm border-b sticky top-0 z-10">
                                            <div>Select User</div>
                                            <div>Commission $</div>
                                            <div>Actions</div>
                                        </div>

                                        {/* Scrollable Body Table */}
                                        <div className="min-h-[160px] max-h-[160px] overflow-y-auto overflow-x-visible">
                                            {overrides.map((override, index) => {
                                                const isEditing = editingRowIndex === index;
                                                const isEmptyRow = !override.override;

                                                return (
                                                    <div
                                                        key={index}
                                                        className="grid grid-cols-1 md:grid-cols-[minmax(200px,2fr)_minmax(150px,1fr)_160px] gap-3 md:gap-4 px-3 py-3 border-b last:border-b-0 items-center"
                                                    >
                                                        {/* Select User Column */}
                                        {isEditing ? (
                                                            <div className="relative">
                                                                <div
                                                                    className="relative"
                                                                    ref={el => overrideInputWrapperRefs.current[index] = el}
                                                                >
                                                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                                                                    <Input
                                                                        className="pl-9"
                                                                        placeholder="Search user..."
                                                                        autoComplete="off"
                                                                        value={overrideSearchTerms[index] || ''}
                                                                        onChange={(e) => {
                                                                            setOverrideSearchTerms(prev => ({ ...prev, [index]: e.target.value }));
                                                                            setShowOverrideDropdowns(prev => ({ ...prev, [index]: true }));
                                                                        }}
                                                                        onFocus={() => {
                                                                            setOverrideDropdownPositions(prev => ({
                                                                                ...prev,
                                                                                [index]: getDropdownPosition(overrideInputWrapperRefs.current[index])
                                                                            }));
                                                                            setShowOverrideDropdowns(prev => ({ ...prev, [index]: true }));
                                                                        }}
                                                                        onBlur={() => setTimeout(() => setShowOverrideDropdowns(prev => ({ ...prev, [index]: false })), 200)}
                                                                    />
                                                                </div>
                                                                {!!showOverrideDropdowns[index] && getFilteredOverrides(index).length > 0 && overrideDropdownPositions[index] && createPortal(
                                                                    <div
                                                                        className="fixed z-[9999] bg-popover border border-border rounded-md shadow-lg max-h-[100px] overflow-y-auto"
                                                                        style={{ ...(overrideDropdownPositions[index].openUpward ? { bottom: overrideDropdownPositions[index].bottom } : { top: overrideDropdownPositions[index].top }), left: overrideDropdownPositions[index].left, width: overrideDropdownPositions[index].width, pointerEvents: 'auto' }}
                                                                    >
                                                                        {getFilteredOverrides(index).map((user, i) => (
                                                                            <button
                                                                                key={i}
                                                                                type="button"
                                                                                className="w-full text-left px-3 py-2 text-sm hover:bg-accent flex flex-col items-start gap-0.5 sm:flex-row sm:items-center sm:justify-between sm:gap-2"
                                                                                onMouseDown={(e) => e.preventDefault()}
                                                                                onClick={() => {
                                                                                    handleOverrideSelectUser(index, user);
                                                                                    setShowOverrideDropdowns(prev => ({ ...prev, [index]: false }));
                                                                                }}
                                                                            >
                                                                                <span className="font-medium">{user.fullName}</span>
                                                                                <span className="text-xs text-muted-foreground">{user.email}</span>
                                                                            </button>
                                                                        ))}
                                                                    </div>,
                                                                    document.body
                                                                )}
                                                                {!!showOverrideDropdowns[index] && (overrideSearchTerms[index] || '').length > 0 && getFilteredOverrides(index).length === 0 && overrideDropdownPositions[index] && createPortal(
                                                                    <div
                                                                        className="fixed z-[9999] bg-popover border border-border rounded-md shadow-lg px-3 py-2 text-sm text-muted-foreground"
                                                                        style={{ ...(overrideDropdownPositions[index].openUpward ? { bottom: overrideDropdownPositions[index].bottom } : { top: overrideDropdownPositions[index].top }), left: overrideDropdownPositions[index].left, width: overrideDropdownPositions[index].width, pointerEvents: 'auto' }}
                                                                    >
                                                                        No user found.
                                                                    </div>,
                                                                    document.body
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-sm">{override.fullName}</span>
                                                                <div className="relative inline-flex items-center gap-1 group">
                                                                    {override.parentType === 'GP' ? (
                                                                        <>
                                                                            <Star className="w-4 h-4 fill-accent text-accent" />
                                                                            <Star className="w-4 h-4 fill-accent text-accent" />
                                                                        </>
                                                                    ) : (
                                                                        <Star className="w-4 h-4 fill-accent text-accent" />
                                                                    )}
                                                                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block bg-black text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                                                                        {override.parentType === 'GP' ? 'Grandparent' : 'Parent'}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Commission Column */}
                                                        <Input
                                                            type="text"
                                                            value={override.comm}
                                                            onChange={(e) => {
                                                                const value = e.target.value;
                                                                if (/^[0-9.]*$/.test(value)) {
                                                                    handleOverrideCommChange(index, value);
                                                                }
                                                            }}
                                                            disabled={!isEditing}
                                                            placeholder="Commission $"
                                                            className={!isEditing ? 'bg-muted' : ''}
                                                        />

                                                        {/* Actions Column */}
                                                        <div className="flex gap-2">
                                                            {isEditing ? (
                                                                <>
                                                                    <Button
                                                                        type="button"
                                                                        size="sm"
                                                                        onClick={() => handleSaveOverride(index)}
                                                                        disabled={savingRowIndex === index}
                                                                        className="flex-1 md:flex-initial"
                                                                    >
                                                                        {savingRowIndex === index && (
                                                                            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                                                        )}
                                                                        {isNewRow ? 'Add' : 'Save'}
                                                                    </Button>
                                                                    <Button
                                                                        type="button"
                                                                        variant="outline"
                                                                        size="sm"
                                                                        onClick={() => handleCancelEditOverride(index)}
                                                                        className="flex-1 md:flex-initial"
                                                                    >
                                                                        Cancel
                                                                    </Button>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <Button
                                                                        type="button"
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        onClick={() => handleEditOverride(index)}
                                                                        disabled={editingRowIndex !== null}
                                                                        className="flex-1 md:flex-initial"
                                                                    >
                                                                        <Edit2 className="h-4 w-4" />
                                                                    </Button>
                                                                    <Button
                                                                        type="button"
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        onClick={() => handleRemoveOverride(index)}
                                                                        disabled={editingRowIndex !== null}
                                                                        className="flex-1 md:flex-initial"
                                                                    >
                                                                        <Trash2 className="h-4 w-4" />
                                                                    </Button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Error Message */}
                            {submitMessage && (
                                <Alert variant="destructive">
                                    <AlertDescription>{submitMessage}</AlertDescription>
                                </Alert>
                            )}
                        </div>
                    )}

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            disabled={saveLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={handleSaveSubmit}
                            disabled={saveLoading || loading}
                        >
                            {saveLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Save & Submit
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {grandparentEditPopup && (
                <GrandparentConfirmDialog
                    open={grandparentEditPopup}
                    onOpenChange={(isOpen) => {
                        if (!isOpen) {
                            setGrandparentEditPopup(false);
                            setGrandparentsList([]);
                            setCurrentOverrideData(null);
                        }
                    }}
                    title={`Grandparents for ${currentOverrideData?.email || getRepEmailForGrandparent(currentOverrideData, {}) || ''}`}
                    grandparentsList={grandparentsList}
                    onConfirm={handleAddGrandParent}
                />
            )}
        </>
    );
};

export default AssignEditGroupDialog;

